import { useState } from 'react'
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
} from '@mui/material'
import {
  Upload,
  Download,
  Visibility,
  Delete,
  Description,
  History,
  Gavel,
  LockOpen,
  CloudUpload,
} from '@mui/icons-material'
import { useAppSelector } from '../hooks/redux'
import {
  useGetMemorialsQuery,
  useSubmitMemorialMutation,
  useLazyDownloadMemorialQuery,
  useDeleteMemorialMutation,
  useGetMemorialDeadlineStatusQuery,
  useLazyGetMemorialVersionsQuery,
  useOfficialSubmitMemorialMutation,
  useReleaseMemorialSubmissionLockMutation,
  type Memorial,
} from '../store/api/memorialsApi'
import { useGetCasesQuery } from '../store/api/casesApi'
import { useGetTeamsQuery } from '../store/api/teamsApi'
import {
  useListRoleSheetsQuery,
  useUploadRoleSheetMutation,
  useDeleteRoleSheetMutation,
  useLazyDownloadRoleSheetQuery,
} from '../store/api/bracketsApi'
import { hasPermission } from '../utils/permissions'
import { formatCaseDisplay } from '../utils/caseDisplay'
import { PageHeader } from '../components/PageHeader'
import SubmissionDeadlineCountdown from '../components/SubmissionDeadlineCountdown'
import { getFilterPaperSx, getTablePaperSx } from '../theme/sectionSurfaces'
import { gradientBrand } from '../theme/brand'
import { getApiErrorMessage } from '../utils/apiErrorMessage'

export default function MemorialsPage() {
  const { user: currentUser } = useAppSelector((state) => state.auth)
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false)
  const [selectedCaseId, setSelectedCaseId] = useState<string>('')
  
  const { data: cases = [] } = useGetCasesQuery()
  const { data: teams = [], isLoading: isLoadingTeams } = useGetTeamsQuery()
  const { data: memorials = [], isLoading, refetch } = useGetMemorialsQuery(
    { case_id: selectedCaseId || undefined },
    { skip: false }
  )

  // ── Bảng phân vai (UAT-TC #4 Memorial) ───────────────────────────────
  const {
    data: roleSheets = [],
    refetch: refetchRoleSheets,
  } = useListRoleSheetsQuery(selectedCaseId || '', { skip: !selectedCaseId })
  const [uploadRoleSheet, { isLoading: isUploadingRoleSheet }] = useUploadRoleSheetMutation()
  const [deleteRoleSheet] = useDeleteRoleSheetMutation()
  const [downloadRoleSheet] = useLazyDownloadRoleSheetQuery()

  const [submitMemorial, { isLoading: isSubmitting }] = useSubmitMemorialMutation()
  const [downloadMemorial] = useLazyDownloadMemorialQuery()
  const [deleteMemorial] = useDeleteMemorialMutation()
  const [officialSubmitMemorial, { isLoading: isOfficialSubmitting }] =
    useOfficialSubmitMemorialMutation()
  const [releaseLock, { isLoading: isReleasingLock }] = useReleaseMemorialSubmissionLockMutation()
  const [fetchVersions, { data: versionRows = [], isFetching: isVersionsLoading }] =
    useLazyGetMemorialVersionsQuery()
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error' | 'warning' | 'info'
  }>({
    open: false,
    message: '',
    severity: 'success',
  })
  
  // View memorial dialog state
  const [viewDialogOpen, setViewDialogOpen] = useState(false)
  const [viewingMemorial, setViewingMemorial] = useState<Memorial | null>(null)

  const [historyDialogOpen, setHistoryDialogOpen] = useState(false)
  const [historyLabel, setHistoryLabel] = useState('')

  const [officialConfirmOpen, setOfficialConfirmOpen] = useState(false)
  const [memorialToOfficial, setMemorialToOfficial] = useState<Memorial | null>(null)

  const [releaseConfirmOpen, setReleaseConfirmOpen] = useState(false)
  const [lockToRelease, setLockToRelease] = useState<{ case_id: string; team_id: string } | null>(
    null
  )

  // Get user's team
  const userTeam = teams.find(
    (t) => t.members.includes(currentUser?.id || '') || t.coach_id === currentUser?.id
  )

  // Filter cases assigned to user's team
  const userCases = cases.filter((c) => {
    if (!userTeam) return false
    return c.assigned_team_ids?.includes(userTeam.id)
  })

  const caseForDeadlineBanner = selectedCaseId || userCases[0]?.id || ''
  const { data: deadlineStatus } = useGetMemorialDeadlineStatusQuery(caseForDeadlineBanner, {
    skip: !caseForDeadlineBanner,
    pollingInterval: 1000,
  })

  const canSubmitMemorial = currentUser && hasPermission(currentUser.role, 'SUBMIT_MEMORIAL')
  const canAssignCases = currentUser && hasPermission(currentUser.role, 'ASSIGN_CASES')
  const canBypassMemorialLock =
    !!currentUser &&
    (hasPermission(currentUser.role, 'ASSIGN_CASES') ||
      hasPermission(currentUser.role, 'MANAGE_USERS'))

  const isOnUserTeam = (teamId: string) =>
    !!userTeam &&
    userTeam.id === teamId &&
    !!currentUser &&
    (userTeam.members.includes(currentUser.id) || userTeam.coach_id === currentUser.id)
  // Upload form state (phiên bản do server gán tự động)
  const [uploadForm, setUploadForm] = useState({
    case_id: '',
    team_id: userTeam?.id || '',
    file: null as File | null,
    title: '',
  })

  const submissionLockedForUpload =
    !!uploadForm.case_id &&
    !!uploadForm.team_id &&
    memorials.some(
      (m) =>
        m.case_id === uploadForm.case_id &&
        m.team_id === uploadForm.team_id &&
        m.submission_locked
    )

  const { data: uploadDeadlineStatus } = useGetMemorialDeadlineStatusQuery(uploadForm.case_id, {
    skip: !uploadDialogOpen || !uploadForm.case_id,
    pollingInterval: uploadDialogOpen && uploadForm.case_id ? 1000 : undefined,
  })

  const handleOpenUploadDialog = () => {
    if (userCases.length > 0) {
      setUploadForm({
        case_id: userCases[0].id,
        team_id: userTeam?.id || '',
        file: null,
        title: '',
      })
    }
    setUploadDialogOpen(true)
  }

  const handleCloseUploadDialog = () => {
    setUploadDialogOpen(false)
    setUploadForm({
      case_id: '',
      team_id: userTeam?.id || '',
      file: null,
      title: '',
    })
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setUploadForm({ ...uploadForm, file: event.target.files[0] })
    }
  }

  // Create memorial function - matches moduleFunctions.ts
  const handleCreate = async () => {
    if (!uploadForm.file || !uploadForm.case_id || !uploadForm.team_id) {
      setSnackbar({
        open: true,
        message: 'Vui lòng điền đầy đủ thông tin',
        severity: 'error',
      })
      return
    }

    if (uploadDeadlineStatus && !uploadDeadlineStatus.submission_open) {
      setSnackbar({
        open: true,
        message: uploadDeadlineStatus.message || 'Hết hạn nộp memorial.',
        severity: 'error',
      })
      return
    }

    if (submissionLockedForUpload) {
      setSnackbar({
        open: true,
        message: 'Đội đã nộp memorial chính thức — không thể nộp thêm trừ khi BTC mở khóa.',
        severity: 'error',
      })
      return
    }

    const formData = new FormData()
    formData.append('file', uploadForm.file)
    formData.append('case_id', uploadForm.case_id)
    formData.append('team_id', uploadForm.team_id)
    if (uploadForm.title) formData.append('title', uploadForm.title)
    formData.append('version', '1')

    try {
      await submitMemorial(formData).unwrap()
      handleCloseUploadDialog()
      refetch() // Refetch danh sách memorials
      setSnackbar({
        open: true,
        message: 'Nộp memorial thành công!',
        severity: 'success',
      })
    } catch (error: unknown) {
      setSnackbar({
        open: true,
        message: `Nộp memorial thất bại: ${getApiErrorMessage(error)}`,
        severity: 'error',
      })
    }
  }

  const handleDownload = async (memorialId: string, fileName: string) => {
    try {
      const result = await downloadMemorial(memorialId).unwrap()
      const url = window.URL.createObjectURL(result)
      const a = document.createElement('a')
      a.href = url
      a.download = fileName
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      setSnackbar({
        open: true,
        message: 'Download thành công!',
        severity: 'success',
      })
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: `Download thất bại: ${error.data?.detail || error.message}`,
        severity: 'error',
      })
    }
  }
  
  // ── UAT-TC #4 Memorial: Upload / Download / Delete Bảng phân vai ────
  const handleUploadRoleSheet = async (file?: File) => {
    if (!file || !selectedCaseId) {
      setSnackbar({
        open: true,
        message: 'Vui lòng chọn cuộc thi và file PDF Bảng phân vai trước.',
        severity: 'warning',
      })
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      setSnackbar({ open: true, message: 'File vượt quá 5MB.', severity: 'error' })
      return
    }
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setSnackbar({ open: true, message: 'Bảng phân vai chỉ chấp nhận PDF.', severity: 'error' })
      return
    }
    try {
      await uploadRoleSheet({
        caseId: selectedCaseId,
        title: file.name.replace(/\.pdf$/i, ''),
        file,
      }).unwrap()
      setSnackbar({
        open: true,
        message: `Nộp Bảng phân vai thành công: ${file.name}. BTC đã được thông báo.`,
        severity: 'success',
      })
      refetchRoleSheets()
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: `Nộp Bảng phân vai thất bại: ${err?.data?.detail || err?.message || 'Lỗi không xác định'}`,
        severity: 'error',
      })
    }
  }

  const handleDownloadRoleSheet = async (sheetId: string, fileName: string) => {
    try {
      const blob = await downloadRoleSheet(sheetId).unwrap()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = fileName || 'role-sheet.pdf'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: `Tải Bảng phân vai thất bại: ${err?.data?.detail || err?.message || ''}`,
        severity: 'error',
      })
    }
  }

  const handleDeleteRoleSheet = async (sheetId: string) => {
    if (!confirm('Xoá Bảng phân vai này?')) return
    try {
      await deleteRoleSheet(sheetId).unwrap()
      setSnackbar({ open: true, message: 'Đã xoá Bảng phân vai.', severity: 'success' })
      refetchRoleSheets()
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: `Xoá thất bại: ${err?.data?.detail || err?.message || ''}`,
        severity: 'error',
      })
    }
  }

  // View memorial function - matches moduleFunctions.ts
  const handleOpenHistory = (memorial: Memorial) => {
    const c = cases.find((x) => x.id === memorial.case_id)
    setHistoryLabel(c ? formatCaseDisplay(c) : memorial.case_id)
    setHistoryDialogOpen(true)
    fetchVersions({ case_id: memorial.case_id, team_id: memorial.team_id })
  }

  const handleCloseHistoryDialog = () => {
    setHistoryDialogOpen(false)
    setHistoryLabel('')
  }

  const handleOfficialClick = (memorial: Memorial) => {
    setMemorialToOfficial(memorial)
    setOfficialConfirmOpen(true)
  }

  const handleOfficialConfirm = async () => {
    if (!memorialToOfficial) return
    try {
      await officialSubmitMemorial(memorialToOfficial.id).unwrap()
      setSnackbar({
        open: true,
        message: 'Đã xác nhận nộp memorial chính thức. Đội không thể nộp thêm hoặc xóa trừ khi BTC mở khóa.',
        severity: 'success',
      })
      refetch()
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.data?.detail || error.message || 'Thao tác thất bại',
        severity: 'error',
      })
    } finally {
      setOfficialConfirmOpen(false)
      setMemorialToOfficial(null)
    }
  }

  const handleReleaseLockClick = (case_id: string, team_id: string) => {
    setLockToRelease({ case_id, team_id })
    setReleaseConfirmOpen(true)
  }

  const handleReleaseLockConfirm = async () => {
    if (!lockToRelease) return
    try {
      await releaseLock({ case_id: lockToRelease.case_id, team_id: lockToRelease.team_id }).unwrap()
      setSnackbar({ open: true, message: 'Đã mở khóa nộp memorial cho đội.', severity: 'success' })
      refetch()
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.data?.detail || error.message || 'Mở khóa thất bại',
        severity: 'error',
      })
    } finally {
      setReleaseConfirmOpen(false)
      setLockToRelease(null)
    }
  }

  const handleView = (memorial: Memorial) => {
    setViewingMemorial(memorial)
    setViewDialogOpen(true)
  }
  
  const handleCloseViewDialog = () => {
    setViewDialogOpen(false)
    setViewingMemorial(null)
  }

  // Delete memorial handler
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [memorialToDelete, setMemorialToDelete] = useState<Memorial | null>(null)

  const handleDeleteClick = (memorial: Memorial) => {
    setMemorialToDelete(memorial)
    setDeleteConfirmOpen(true)
  }

  // Delete memorial function - matches moduleFunctions.ts
  const handleDelete = async () => {
    if (!memorialToDelete) return

    try {
      await deleteMemorial(memorialToDelete.id).unwrap()
      setSnackbar({
        open: true,
        message: 'Xóa memorial thành công!',
        severity: 'success',
      })
      refetch()
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: `Xóa memorial thất bại: ${error.data?.detail || error.message}`,
        severity: 'error',
      })
    } finally {
      setDeleteConfirmOpen(false)
      setMemorialToDelete(null)
    }
  }

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false)
    setMemorialToDelete(null)
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'submitted':
        return 'success'
      case 'draft':
        return 'default'
      default:
        return 'info'
    }
  }

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 } }}>
      <PageHeader
        title="Quản lý Memorial"
        subtitle="Nộp bản memorial, theo dõi hạn chót và lịch sử phiên bản theo từng cuộc thi."
        icon={<Description />}
        actions={
          canSubmitMemorial && userTeam ? (
            <Button
              variant="contained"
              color="primary"
              startIcon={<Upload />}
              onClick={handleOpenUploadDialog}
              disabled={
                userCases.length === 0 ||
                (!!deadlineStatus && !deadlineStatus.submission_open && !!caseForDeadlineBanner)
              }
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                background: gradientBrand,
                '&:hover': { background: gradientBrand, filter: 'brightness(1.06)' },
              }}
            >
              Nộp Memorial
            </Button>
          ) : null
        }
      />

      {!canSubmitMemorial && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Chỉ sinh viên (students) mới có quyền nộp memorial cho case được assign cho đội của mình.
        </Alert>
      )}

      {canSubmitMemorial && userCases.length === 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Bạn chưa có case nào được assign cho đội của mình.
        </Alert>
      )}

      {/* Filter */}
      <Paper elevation={0} sx={(theme) => getFilterPaperSx(theme)}>
        <FormControl fullWidth>
          <InputLabel>Lọc theo cuộc thi</InputLabel>
          <Select
            value={selectedCaseId}
            label="Lọc theo cuộc thi"
            onChange={(e) => setSelectedCaseId(e.target.value)}
          >
            <MenuItem value="">Tất cả</MenuItem>
            {userCases.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {formatCaseDisplay(c)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      {deadlineStatus && !deadlineStatus.submission_open && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Hết hạn nộp bài
          </Typography>
          <Typography variant="body2">{deadlineStatus.message}</Typography>
          <SubmissionDeadlineCountdown status={deadlineStatus} />
        </Alert>
      )}

      {deadlineStatus && deadlineStatus.submission_open && caseForDeadlineBanner && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {deadlineStatus.message}
          <SubmissionDeadlineCountdown status={deadlineStatus} />
        </Alert>
      )}

      {userTeam &&
        memorials.some(
          (m) => m.team_id === userTeam.id && m.submission_locked
        ) && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Đội đã nộp memorial chính thức — không thể nộp bản mới hoặc xóa. Nếu cần chỉnh sửa ngoại lệ,
            liên hệ BTC để mở khóa.
          </Alert>
        )}

      {/* Memorials Table */}
      <Paper elevation={0} sx={(theme) => getTablePaperSx(theme)}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Cuộc thi</TableCell>
                <TableCell>Đội thi</TableCell>
                <TableCell>File</TableCell>
                <TableCell>Kích thước</TableCell>
                <TableCell>Version</TableCell>
                <TableCell>Trạng thái</TableCell>
                <TableCell>Nộp chính thức</TableCell>
                <TableCell>Ngày nộp</TableCell>
                <TableCell align="right">Thao tác</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading || isLoadingTeams ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : memorials.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography variant="body2" color="text.secondary">
                      Chưa có memorial nào
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                memorials.map((memorial) => {
                  const caseItem = cases.find((c) => c.id === memorial.case_id)
                  const team = teams.find((t) => t.id === memorial.team_id)
                  // Đảm bảo hiển thị tên đội, nếu không tìm thấy thì hiển thị ID
                  const teamName = team ? team.name : memorial.team_id
                  const deleteDisabled = !!memorial.submission_locked && !canBypassMemorialLock
                  return (
                    <TableRow key={memorial.id}>
                      <TableCell>{caseItem ? formatCaseDisplay(caseItem) : memorial.case_id}</TableCell>
                      <TableCell>{teamName}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Description />
                          <Typography variant="body2">{memorial.file_name}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{(memorial.file_size / 1024 / 1024).toFixed(2)} MB</TableCell>
                      <TableCell>{memorial.version || 1}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          <Chip
                            label={memorial.status}
                            color={getStatusColor(memorial.status)}
                            size="small"
                          />
                          {memorial.submission_locked && (
                            <Chip label="Đã khóa đội" color="warning" size="small" variant="outlined" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {memorial.is_official_submission ? (
                          <Chip label="Bản được chọn" size="small" color="success" variant="outlined" />
                        ) : memorial.submission_locked ? (
                          <Typography variant="caption" color="text.secondary">
                            Bản lưu
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {memorial.submitted_at
                          ? new Date(memorial.submitted_at).toLocaleString('vi-VN')
                          : '-'}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleDownload(memorial.id, memorial.file_name)}
                          title="Download"
                        >
                          <Download />
                        </IconButton>
                        <IconButton
                          size="small"
                          title="Xem chi tiết"
                          onClick={() => handleView(memorial)}
                        >
                          <Visibility />
                        </IconButton>
                        <IconButton
                          size="small"
                          title="Lịch sử phiên bản"
                          onClick={() => handleOpenHistory(memorial)}
                        >
                          <History />
                        </IconButton>
                        {isOnUserTeam(memorial.team_id) && !memorial.submission_locked && (
                          <IconButton
                            size="small"
                            title="Nộp memorial chính thức (khóa sửa/xóa đội)"
                            color="primary"
                            onClick={() => handleOfficialClick(memorial)}
                          >
                            <Gavel />
                          </IconButton>
                        )}
                        {canAssignCases && memorial.submission_locked && (
                          <IconButton
                            size="small"
                            title="Mở khóa nộp memorial (BTC)"
                            color="secondary"
                            onClick={() =>
                              handleReleaseLockClick(memorial.case_id, memorial.team_id)
                            }
                          >
                            <LockOpen />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteClick(memorial)}
                          title={deleteDisabled ? 'Đã khóa — chỉ BTC mới xóa được' : 'Xóa'}
                          color="error"
                          disabled={deleteDisabled}
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* UAT-TC #4 Memorial: Bảng phân vai (Role Assignment Sheet) */}
      {selectedCaseId && (
        <Paper elevation={0} sx={(theme) => ({ ...getFilterPaperSx(theme), mt: 3 })}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { sm: 'center' }, gap: 2, mb: 2 }}>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Bảng phân vai
              </Typography>
              <Typography variant="caption" color="text.secondary">
                File PDF phân công vai trò luật sư / nguyên đơn / bị đơn cho đội. Tối đa 5MB.
              </Typography>
            </Box>
            {canSubmitMemorial && userTeam && (
              <Button
                component="label"
                variant="contained"
                startIcon={<CloudUpload />}
                disabled={isUploadingRoleSheet}
                sx={{ background: gradientBrand, textTransform: 'none', alignSelf: { xs: 'stretch', sm: 'center' } }}
              >
                {isUploadingRoleSheet ? 'Đang nộp...' : 'Nộp Bảng phân vai'}
                <input
                  hidden
                  type="file"
                  accept=".pdf"
                  onChange={(e) => handleUploadRoleSheet(e.target.files?.[0])}
                />
              </Button>
            )}
          </Box>
          {roleSheets.length === 0 ? (
            <Alert severity="info">
              Chưa có Bảng phân vai cho cuộc thi này.
              {canSubmitMemorial && userTeam && ' Đội bạn có thể nộp bằng nút bên trên.'}
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 700 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ minWidth: 220 }}><strong>Tên file</strong></TableCell>
                    <TableCell sx={{ minWidth: 140 }}><strong>Đội nộp</strong></TableCell>
                    <TableCell sx={{ minWidth: 100 }}><strong>Vai trò</strong></TableCell>
                    <TableCell sx={{ minWidth: 100 }}><strong>Kích thước</strong></TableCell>
                    <TableCell sx={{ minWidth: 130 }}><strong>Ngày nộp</strong></TableCell>
                    <TableCell sx={{ minWidth: 110 }}><strong>Trạng thái</strong></TableCell>
                    <TableCell align="right" sx={{ minWidth: 130 }}><strong>Thao tác</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {roleSheets.map((s) => {
                    const team = teams.find((t) => t.id === s.team_id)
                    const isOwn = userTeam && s.team_id === userTeam.id
                    return (
                      <TableRow key={s.id} hover>
                        <TableCell>{s.title || s.file_name}</TableCell>
                        <TableCell>{team?.name || (s.team_id ? s.team_id : '— BTC —')}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={s.uploader_role || (s.team_id ? 'team' : 'btc')}
                            color={s.team_id ? 'primary' : 'default'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{(s.file_size / 1024).toFixed(1)} KB</TableCell>
                        <TableCell>
                          {s.uploaded_at
                            ? new Date(s.uploaded_at).toLocaleString('vi-VN')
                            : '-'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={s.team_id ? 'Đã nộp Bảng phân vai' : 'BTC ban hành'}
                            color={s.team_id ? 'success' : 'info'}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => s.id && handleDownloadRoleSheet(s.id, s.file_name)}
                            title="Tải xuống"
                          >
                            <CloudUpload sx={{ transform: 'rotate(180deg)' }} fontSize="small" />
                          </IconButton>
                          {isOwn && s.id && (
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteRoleSheet(s.id!)}
                              color="error"
                              title="Xoá"
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* Upload Dialog */}
      <Dialog 
        open={uploadDialogOpen} 
        onClose={handleCloseUploadDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: { xs: 1, sm: 2 },
            m: { xs: 1, sm: 2 },
          }
        }}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, fontWeight: 600 }}>
          Nộp Memorial
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: { xs: 1, sm: 2 } }}>
            <FormControl fullWidth required>
              <InputLabel>Cuộc thi *</InputLabel>
              <Select
                value={uploadForm.case_id}
                label="Cuộc thi *"
                onChange={(e) => setUploadForm({ ...uploadForm, case_id: e.target.value })}
                required
              >
                {userCases.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {formatCaseDisplay(c)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Tiêu đề (tùy chọn)"
              value={uploadForm.title}
              onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
            />
            <Typography variant="body2" color="text.secondary">
              Số phiên bản do hệ thống tự tăng mỗi lần nộp file (lịch sử xem tại nút biểu tượng lịch sử).
            </Typography>
            <Button variant="outlined" component="label" fullWidth startIcon={<Upload />}>
              Chọn File (PDF hoặc DOCX)
              <input type="file" hidden accept=".pdf,.docx" onChange={handleFileChange} />
            </Button>
            {uploadForm.file && (
              <Typography variant="body2">
                Đã chọn: {uploadForm.file.name} ({(uploadForm.file.size / 1024 / 1024).toFixed(2)} MB)
              </Typography>
            )}
            {submissionLockedForUpload && (
              <Alert severity="warning">
                Đội đã nộp memorial chính thức — không thể nộp thêm cho đến khi BTC mở khóa.
              </Alert>
            )}
            {uploadDeadlineStatus && (
              <Alert severity={uploadDeadlineStatus.submission_open ? 'info' : 'error'}>
                {uploadDeadlineStatus.submission_open
                  ? uploadDeadlineStatus.message
                  : `Hết hạn nộp bài. ${uploadDeadlineStatus.message}`}
                <SubmissionDeadlineCountdown status={uploadDeadlineStatus} />
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, gap: 1 }}>
          <Button 
            onClick={handleCloseUploadDialog}
            sx={{ textTransform: 'none', fontWeight: 500 }}
          >
            Hủy
          </Button>
          <Button 
            onClick={handleCreate} 
            variant="contained" 
            disabled={
              isSubmitting ||
              submissionLockedForUpload ||
              !!(uploadDeadlineStatus && !uploadDeadlineStatus.submission_open)
            }
            sx={{ textTransform: 'none', fontWeight: 500, borderRadius: 1 }}
          >
            {isSubmitting ? <CircularProgress size={20} /> : 'Nộp Memorial'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Memorial Dialog */}
      <Dialog 
        open={viewDialogOpen} 
        onClose={handleCloseViewDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: { xs: 1, sm: 2 },
            m: { xs: 1, sm: 2 },
          }
        }}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, fontWeight: 600 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Description />
            <Typography variant="h6">Chi tiết Memorial</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
          {viewingMemorial && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Tiêu đề
                </Typography>
                <Typography variant="body1">{viewingMemorial.title || viewingMemorial.file_name}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Cuộc thi
                </Typography>
                <Typography variant="body1">
                  {cases.find((c) => c.id === viewingMemorial.case_id) 
                    ? formatCaseDisplay(cases.find((c) => c.id === viewingMemorial.case_id)!)
                    : viewingMemorial.case_id}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Đội thi
                </Typography>
                <Typography variant="body1">
                  {(() => {
                    const team = teams.find((t) => t.id === viewingMemorial.team_id)
                    return team ? team.name : (isLoadingTeams ? 'Đang tải...' : viewingMemorial.team_id)
                  })()}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  File
                </Typography>
                <Typography variant="body1">{viewingMemorial.file_name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Kích thước: {(viewingMemorial.file_size / 1024 / 1024).toFixed(2)} MB
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Version
                </Typography>
                <Chip label={viewingMemorial.version || 1} size="small" />
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Trạng thái
                </Typography>
                <Chip
                  label={viewingMemorial.status}
                  color={getStatusColor(viewingMemorial.status)}
                  size="small"
                />
              </Box>
              {viewingMemorial.submitted_at && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Ngày nộp
                  </Typography>
                  <Typography variant="body1">
                    {new Date(viewingMemorial.submitted_at).toLocaleString('vi-VN')}
                  </Typography>
                </Box>
              )}
              {viewingMemorial.tags && viewingMemorial.tags.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Tags
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {(viewingMemorial.tags ?? [])
                      .filter((tag: string) => {
                        const tagList = viewingMemorial.tags ?? []
                        // Bỏ qua team_id và case_id nếu đã có team: và case:
                        if (tag.startsWith('team_id:') && tagList.some((t: string) => t.startsWith('team:'))) {
                          return false
                        }
                        if (tag.startsWith('case_id:') && tagList.some((t: string) => t.startsWith('case:'))) {
                          return false
                        }
                        return true
                      })
                      .map((tag: string, idx: number) => {
                        // Format tag để hiển thị tên thay vì ID
                        let displayTag = tag
                        if (tag.startsWith('team:')) {
                          displayTag = tag.replace('team:', 'Đội: ')
                        } else if (tag.startsWith('case:')) {
                          displayTag = tag.replace('case:', 'Cuộc thi: ')
                        } else if (tag.startsWith('case_id:')) {
                          const caseId = tag.replace('case_id:', '')
                          const caseItem = cases.find((c) => c.id === caseId)
                          displayTag = caseItem ? `Cuộc thi: ${formatCaseDisplay(caseItem)}` : tag
                        } else if (tag.startsWith('team_id:')) {
                          const teamId = tag.replace('team_id:', '')
                          const team = teams.find((t) => t.id === teamId)
                          displayTag = team ? `Đội: ${team.name}` : tag
                        } else if (tag.startsWith('case_number:')) {
                          displayTag = tag.replace('case_number:', 'Số vụ án: ')
                        }
                        return (
                          <Chip key={idx} label={displayTag} size="small" variant="outlined" />
                        )
                      })}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {viewingMemorial && (
            <Button
              startIcon={<Download />}
              onClick={() => handleDownload(viewingMemorial.id, viewingMemorial.file_name)}
            >
              Download
            </Button>
          )}
          <Button onClick={handleCloseViewDialog}>Đóng</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={historyDialogOpen} onClose={handleCloseHistoryDialog} maxWidth="md" fullWidth>
        <DialogTitle>Lịch sử phiên bản memorial</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {historyLabel}
          </Typography>
          {isVersionsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : versionRows.length === 0 ? (
            <Typography variant="body2">Chưa có bản ghi phiên bản.</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Ver.</TableCell>
                    <TableCell>File</TableCell>
                    <TableCell>Ngày nộp</TableCell>
                    <TableCell>Ghi chú</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {versionRows.map((row) => (
                    <TableRow key={row.memorial_id}>
                      <TableCell>{row.version}</TableCell>
                      <TableCell>{row.file_name}</TableCell>
                      <TableCell>
                        {row.submitted_at
                          ? new Date(row.submitted_at).toLocaleString('vi-VN')
                          : '—'}
                      </TableCell>
                      <TableCell>
                        {row.is_official_submission ? (
                          <Chip size="small" label="Bản chính thức" color="success" variant="outlined" />
                        ) : (
                          '—'
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseHistoryDialog}>Đóng</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={officialConfirmOpen} onClose={() => setOfficialConfirmOpen(false)}>
        <DialogTitle>Xác nhận nộp memorial chính thức</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Sau khi xác nhận, đội không thể nộp thêm bản memorial mới hoặc xóa bản đã nộp, trừ khi Ban tổ
            chức mở khóa ngoại lệ. Bạn chắc chắn muốn chọn bản này là bản chính thức?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOfficialConfirmOpen(false)}>Hủy</Button>
          <Button variant="contained" onClick={handleOfficialConfirm} disabled={isOfficialSubmitting}>
            {isOfficialSubmitting ? <CircularProgress size={20} /> : 'Xác nhận'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={releaseConfirmOpen} onClose={() => setReleaseConfirmOpen(false)}>
        <DialogTitle>Mở khóa nộp memorial (BTC)</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Đội sẽ được phép nộp bản memorial mới và xóa bản cũ trở lại. Tiếp tục?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReleaseConfirmOpen(false)}>Hủy</Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleReleaseLockConfirm}
            disabled={isReleasingLock}
          >
            {isReleasingLock ? <CircularProgress size={20} /> : 'Mở khóa'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog 
        open={deleteConfirmOpen} 
        onClose={handleDeleteCancel}
        PaperProps={{
          sx: {
            borderRadius: { xs: 1, sm: 2 },
            m: { xs: 1, sm: 2 },
          }
        }}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, fontWeight: 600 }}>
          Xác nhận xóa
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
          <Typography>
            Bạn có chắc muốn xóa memorial "{memorialToDelete?.title || memorialToDelete?.file_name}"?
            Hành động này không thể hoàn tác.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, gap: 1 }}>
          <Button 
            onClick={handleDeleteCancel}
            sx={{ textTransform: 'none', fontWeight: 500 }}
          >
            Hủy
          </Button>
          <Button 
            onClick={handleDelete} 
            color="error" 
            variant="contained"
            sx={{ textTransform: 'none', fontWeight: 500, borderRadius: 1 }}
          >
            Xóa
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  )
}
